import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { GroupByDateTypes, TimesheetsFeatureService } from '@features';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { getEndDateOfTheMonth, getEndDateOfTheWeek, getStartDateOfTheMonth, getStartDateOfTheWeek } from '@utils';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import * as _moment from 'moment';
import { default as _rollupMoment, Moment } from 'moment';

const moment = _rollupMoment || _moment;
moment.updateLocale(moment.locale(), { week: { dow: 1, doy: 4 } });

const DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: { dateInput: 'DD/MM/YYYY', monthYearLabel: 'MMM YYYY', dateA11yLabel: 'LL', monthYearA11yLabel: 'MMMM YYYY' },
};

@Component({
  selector: 'app-timesheet-filters',
  standalone: true,
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonToggleModule,
    MatButton,
    MatIconButton,
    MatIconModule,
  ],
  providers: [provideMomentDateAdapter(DATE_FORMATS)],
  templateUrl: './timesheet-filters.component.html',
  styleUrl: './timesheet-filters.component.css',
})
export class TimesheetFiltersComponent implements OnInit {
  _timesheetFeature = inject(TimesheetsFeatureService);

  @Output() dateFilterChanged = new EventEmitter<void>();

  readonly dateInput = new FormControl(moment(this._timesheetFeature.filterTimesheetLines().startDate.toDate()));
  readonly groupByDateTypeInput = new FormControl<GroupByDateTypes>(this._timesheetFeature.groupByDateTypes());
  readonly searchControl = new FormControl<string>(this._timesheetFeature.searchText());

  constructor() {
    merge(this.dateInput.statusChanges, this.dateInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateDateFilter());

    merge(this.groupByDateTypeInput.statusChanges, this.groupByDateTypeInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateGroupByDateType());

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this._timesheetFeature.searchText.set(value ?? ''));
  }

  ngOnInit() {}

  updateDateFilter() {
    if (!this.dateInput.value) return;
    const date = this.dateInput.value.toDate();
    const groupType = this.groupByDateTypeInput.value;
    if (groupType === 'week') {
      this._timesheetFeature.filterTimesheetLines.update((value) => ({
        ...value,
        startDate: dayjs(getStartDateOfTheWeek(date)),
        endDate: dayjs(getEndDateOfTheWeek(date)),
      }));
    } else if (groupType === 'month') {
      this._timesheetFeature.filterTimesheetLines.update((value) => ({
        ...value,
        startDate: dayjs(getStartDateOfTheMonth(date)),
        endDate: dayjs(getEndDateOfTheMonth(date)),
      }));
    } else {
      this._timesheetFeature.filterTimesheetLines.update((value) => ({
        ...value,
        startDate: dayjs(date),
        endDate: dayjs(date),
      }));
    }
    this.dateFilterChanged.emit();
  }

  updateGroupByDateType() {
    this._timesheetFeature.groupByDateTypes.set(this.groupByDateTypeInput?.value ?? 'day');
    this.updateDateFilter();
  }

  resetFilters() {
    this.dateInput.patchValue(moment());
    this.groupByDateTypeInput.patchValue('day');
    this.searchControl.setValue('');
  }
}
